import {
  addMinutes,
  addSeconds,
  differenceInSeconds,
  endOfDay,
  endOfMonth,
  endOfToday,
  format,
  formatDistance,
  Locale,
  parseISO,
  startOfDay,
  startOfMonth,
  sub,
  toDate,
} from 'date-fns';
import { de as deLang, es as esLang, fr as frLang } from 'date-fns/locale';
import { DateRange } from '../enums/Date';

/**
 * @see https://date-fns.org/v2.29.3/docs/format
 */
export const DATE_FORMAT = 'MMM dd, yyyy';
export const DATE_FORMAT_SHORT = 'MMM dd';
export const DATE_ISO_FORMAT = 'yyyy-MM-dd';
export const DATETIME_FORMAT = 'MM/dd/yyyy hh:mm aaa';
export const DATETIME_FORMAT_YY_MM_DD = 'yyyy/MM/dd';
export const DATETIME_FORMAT_EXPANDED = "MM/dd/yyyy 'at' hh:mm a";
/**
 * E.g.: Jul 18, 03:00pm
 */
export const DATETIME_FORMAT_SHORT = 'MMM dd, hh:mm aaa';
/**
 * E.g.: Jul 18, 3:00pm (without leading zero)
 */
export const DATETIME_FORMAT_SHORT_V2 = 'MMM dd, h:mm aaa';

// custom formats
export const TRIAL_END_DATE_FORMAT = `${DATE_FORMAT} hh:mm aaa`;

export const formatDateTime = (
  date: number,
  sFormat = DATETIME_FORMAT,
  language?: string
): string => {
  const dateValue = toDate(date * 1000);

  return format(dateValue, sFormat || DATETIME_FORMAT, {
    locale: getDateLanguage(language),
  });
};

/**
 * Gets the seconds since epoch from a given date. This basically divides the
 * date time, which comes in milliseconds to the seconds ellapse since epoch.
 */
export const getEpochFromString = (date: string | null): number | null => {
  if (!date) {
    return null;
  }

  const milliseconds = new Date(date).getTime();
  return Math.floor(milliseconds / 1000);
};

/**
 * Converts a number representation of a date as the seconds ellapsed from
 * epoch, into a valid date. This basically multiples per 1000 because the
 * date object expects milliseconds instead of seconds
 */
export const getDateFromEpoch = (date: number) => new Date(date * 1000);

/**
 * Converts an ISO 8601 date (yyyy-mm-dd) to a localized date object
 */
export const getDateFromISO = (date: string) => {
  const [year, month, days] = date.split('-');
  return new Date(+year, +month - 1, +days);
};

/**
 * Converts an epoch representation of a date into an unlocalized date.
 * This works for epoch representing a date (yyyy-mm-dd) and not a date-time
 * (yyyy-mm-ddThh:mm:ssZ), which the browser will localize.
 */
export const getUnlocalizedDateFromEpoch = (date: number) =>
  addMinutes(new Date(date * 1000), new Date().getTimezoneOffset());

/**
 * Wrapper for the date-fns `formatDistance` method, but with "manual" string
 * replacement to adjust the result, since date-fns doesn't allow further
 * configuration for the generated string.
 */
export const getFormattedDistance = (date: Date, language?: string) =>
  formatDistance(date, new Date(), {
    addSuffix: true,
    locale: getDateLanguage(language),
  })
    .replace('about', '')
    .replace('less than a minute ago', 'just now')
    .replace('in less than a minute', 'just now');

/**
 * Computes the period according to the given date range, to return the
 * start and end dates. For example: Assuming today is 6/23/2021:
 *  - Custom
 *  - Last 7 days: the 7 days ending today (June 17-23)
 *  - Last 30 days: the 30 days ending today (May 25 - June 23)
 *  - Last month: the last calendar month (May 1 - May 31)
 *  - Last 90 days : the last 90 days ending today (March 26 - June 23)
 *  - Last 3 months : the last 3 calendar months (March 1 - May 31)
 *  - Last 12 months: the last 12 calendar months (June 1, 2020 - May 31, 2021)
 *  - All time
 */
export const getDateRangeValues = (
  dateRange: DateRange
): [string | null, string | null] => {
  const today = new Date();
  const lastMonth = sub(today, { months: 1 });

  let startDate: Date | null = today;
  let endDate: Date | null = null;

  // Assuming today is 6/23/2021
  switch (dateRange) {
    // Last 7 days: the 7 days ending today at 23:59:59 (June 17-23)
    case DateRange.Last7Days:
      endDate = endOfToday();
      startDate = startOfDay(sub(endDate, { days: 6 }));
      break;
    // Last 30 days: the last 30 days ending today at 23:59:59 (May 25 - June 23)
    case DateRange.Last30Days:
      endDate = endOfToday();
      startDate = startOfDay(sub(endDate, { days: 29 }));
      break;
    // Last month: the last calendar month (May 1 - May 31)
    case DateRange.LastMonth:
      endDate = endOfDay(endOfMonth(lastMonth));
      startDate = startOfMonth(lastMonth);
      break;
    // Last 90 days: the last 90 days ending today at 23:59:59 (March 26 - June 23)
    case DateRange.Last90Days:
      endDate = endOfToday();
      startDate = startOfDay(sub(endDate, { days: 89 }));
      break;
    // Last 3 months: the last 3 calendar months (March 1 - May 31)
    case DateRange.Last3Months:
      endDate = endOfMonth(lastMonth);
      startDate = startOfMonth(sub(endDate, { months: 2 }));
      break;
    // Last 12 months: the last 12 calendar months (June 1, 2020 - May 31, 2021)
    case DateRange.Last12Months:
      endDate = endOfMonth(lastMonth);
      startDate = startOfMonth(sub(endDate, { months: 11 }));
      break;
    default:
      endDate = null;
      startDate = null;
  }

  return [startDate?.toISOString() || null, endDate?.toISOString() || null];
};

/**
 * Gets the name of the time of the day based on the following ranges:
 *  - 5am - 11:59am -> morning
 *  - 12pm - 4:59pm -> afternoon
 *  - 5pm - 4:59am -> evening
 */
export const getTimeOfDay = (): string => {
  const now = new Date().getHours();

  if (now >= 5 && now < 12) {
    return 'morning';
  }

  if (now >= 12 && now < 17) {
    return 'afternoon';
  }

  return 'evening';
};

export const greet = (): string => `Good ${getTimeOfDay()}!`;

export const durationFormat = (seconds: number): string => {
  const helperDate = addSeconds(new Date(0), seconds);
  return format(helperDate, 'mm:ss');
};

export const getDifferenceInSeconds = (
  startDate: number,
  endDate: number
): number | null => {
  if (!(startDate && endDate)) {
    return null;
  }
  return differenceInSeconds(startDate, endDate);
};

export const getDateLanguage = (language?: string): Locale | undefined => {
  const languages: Record<string, Locale> = {
    de: deLang,
    es: esLang,
    fr: frLang,
  };

  return language ? languages[language] : undefined;
};

export const convertISODate = (isoDate: string): string => {
  const date = parseISO(isoDate);
  return format(date, DATETIME_FORMAT_YY_MM_DD);
};

/**
 * Checks if a given date value is a valid date, by checking if it's a Date
 * object, it's not "Invalid Date" and contains a year with 4 digits
 */
export const isValidDate = (date: any): boolean =>
  date instanceof Date &&
  !Number.isNaN(+date) &&
  `${date.getFullYear()}`.length === 4;
